import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Icon, Progress, TextField, Typography } from '@equinor/eds-core-react'
import { help_outline } from '@equinor/eds-icons'
import { Sensor } from '../../models'
import { updateSensorFrequency } from '@/api/apiService'
import { useQueryClient } from '@tanstack/react-query'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { baseFont } from '@/styles'
import { InputStyledLabel } from '@/styles/layout'
import PopoverDialog from '@/components/popover/PopoverDialog'
import { useDownlinkConfirmDialog } from './useDownlinkConfirmDialog'
import { useSensorConfigurations } from '../../hooks/useSensorConfigurations'
import { QueryKeys } from '@/core/models/enum'

interface Props {
    sensor: Sensor
    supportedTypes: string[]
    onBack: () => void
}

function DownlinkConfiguration({ sensor, supportedTypes, onBack }: Props) {
    const [selectedType] = useState(0)
    const [seconds, setSeconds] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [isTooltipOpen, setTooltipOpen] = useState(false)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const queryClient = useQueryClient()
    const { data: configurations } = useSensorConfigurations(sensor)

    const secondsValue = Number(seconds)
    const isValid = seconds !== '' && Number.isInteger(secondsValue) && secondsValue > 0
    const hasMeasurement = supportedTypes.includes('Measurement')

    useEffect(() => {
        const existingConfiguration = configurations?.pendingConfiguration ?? configurations?.confirmed
        if (existingConfiguration) {
            setSeconds(String(existingConfiguration.seconds))
        }
    }, [configurations])

    const handleConfirmSave = async () => {
        if (!sensor.plant || !sensor.sensorDbKey) return
        setIsSaving(true)
        try {
            await updateSensorFrequency(sensor.plant, sensor.sensorDbKey, secondsValue, selectedType)
            showSuccessDialog()
            const queryKey = [QueryKeys.Configurations, sensor.sensorDbKey] as const
            queryClient.invalidateQueries({ queryKey })
        } catch {
            addSnackBarMessage('Failed to send configuration.')
        } finally {
            setIsSaving(false)
        }
    }

    const { showConfirm: showSuccessDialog } = useDownlinkConfirmDialog(
        {
            title: 'Configuration changes saved',
            content1: 'It might take up to 24 hours for the configuration changes to take effect.',
            confirmButtonTitle: 'Ok',
            hideRejectButton: true,
            onConfirm: onBack,
        },
        []
    )

    const { showConfirm } = useDownlinkConfirmDialog(
        {
            title: 'Save configuration',
            content1: (
                <>
                    The values you&apos;ve set will make the sensor send messages every{' '}
                    <strong>{seconds} seconds</strong>.
                </>
            ),
            content2: 'Do you want to save these changes?',
            confirmButtonTitle: 'Save',
            rejectButtonTitle: 'Cancel',
            onConfirm: handleConfirmSave,
        },
        [seconds, sensor.tag]
    )

    return (
        <Container>
            <Subheading variant="h3">{sensor.tag}</Subheading>
            <HeadingRow>
                <Subheading variant="h4">Measurement intervals</Subheading>
                <div ref={tooltipRef}>
                    <Button
                        variant="ghost_icon"
                        aria-label="Measurement intervals info"
                        onClick={() => setTooltipOpen(!isTooltipOpen)}
                    >
                        <Icon data={help_outline} size={24} />
                    </Button>
                </div>
            </HeadingRow>
            <PopoverDialog
                open={isTooltipOpen}
                anchorEl={tooltipRef.current}
                onClose={() => setTooltipOpen(false)}
                title="Measurement intervals"
                content="Configure how often the sensor takes measurements. Enter the interval in seconds."
            />
            {hasMeasurement && (
                <Section>
                    <StyledTextField
                        id="downlink-seconds"
                        label="Measurement interval"
                        type="number"
                        value={seconds}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSeconds(e.target.value)}
                        helperText="Range: 1 - 28800 (8h)"
                        meta={
                            <RowStyle>
                                <InputStyledLabel>Seconds</InputStyledLabel>
                            </RowStyle>
                        }
                        min={1}
                        max={28800}
                    />
                </Section>
            )}

            <RowContainerBottom>
                <Button as="button" variant="outlined" onClick={onBack} disabled={isSaving}>
                    <Label>Cancel</Label>
                </Button>
                <Button as="button" onClick={showConfirm} disabled={!isValid || !hasMeasurement || isSaving}>
                    {isSaving ? <Progress.Dots /> : <StepperStyledActionLabel>Save</StepperStyledActionLabel>}
                </Button>
            </RowContainerBottom>
        </Container>
    )
}

export default DownlinkConfiguration

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const HeadingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const RowStyle = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
`

const Subheading = styled(Typography)<{
    $marginBottom?: string
    $marginTop?: string
    $fontWeight?: string
}>`
    margin-top: ${({ $marginTop }) => ($marginTop ? $marginTop : '0px')};
    margin-bottom: ${({ $marginBottom }) => ($marginBottom ? $marginBottom : '0px')};
    font-weight: ${({ $fontWeight }) => ($fontWeight ? $fontWeight : 'normal')};
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const StyledTextField = styled(TextField)`
    width: 100%;
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px;
    margin-bottom: 0px;
    gap: 16px;
`

const StepperStyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    &:hover {
        color: white;
        background: #007079;
    }
`

const Label = styled.span`
    ${baseFont}
`
